import React from "react";
import { Formik, Field } from "formik";
import { Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import { formikReactSelectInput } from "../../../../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { ReduxState } from "../../../../../redux";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { setSelectedAccount } from "../../../../../redux/reducers/TransactionManager";
import { AccountForm2 } from "./NewAccount";
import { PeriodForm2 } from "./NewPeriod";
import { createAlert } from "../../../../../redux/reducers/Alerts";
import {
  performBulkEdit,
  performBulkEditTagLevel,
} from "../../../../../docuclipper/api";
import {
  fetchMfs,
  setSelectedRowIds,
  setTls,
  toggleLoading,
} from "../../../../../redux/reducers/TransactionManager";
import { fetchTotals } from "../../../../../redux/reducers/Reconciler";
import { setSelectedChunk } from "../../../../../redux/reducers/TransactionManager";
import uuidv4 from "uuid/v4";
import { useChunkData } from "../../../Analyze/AnalyzeTransactionsTable/hooks";

const validationSchema = Yup.object().shape({
  chunk: Yup.object()
    .nullable()
    .when("isNewAccount", {
      is: false,
      then: Yup.object().nullable().required("Please select an account/period"),
      otherwise: Yup.object().nullable(),
    }),
});

export const SelectAccount2 = ({
  transactionIds,
  setSelectionError,
  onDone,
}) => {
  const { job } = useSelector((state: ReduxState) => state.JobData);
  const { selectedL1: l1, selectedL2: l2 } = useSelector(
    (state: ReduxState) => state.TagLevel
  );

  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    const {
      account,
      isNewAccount,
      periodId,
      startDate,
      endDate,
      startBalance,
      endBalance,
      chunk,
    } = values;

    // Determine account and chunk values based on whether we're using existing or new

    dispatch(toggleLoading());
    const fn =
      l1 && l2
        ? performBulkEditTagLevel
        : job && job.id
        ? performBulkEdit
        : Promise.resolve({ tranasctions: [] });
    const extraArgs =
      l1 && l2 ? { l1, l2 } : job && job.id ? { jobId: job.id } : {};

    const periodData = isNewAccount
      ? { startDate, endDate, startBalance, endBalance }
      : {
          startDate: chunk.startDate,
          endDate: chunk.endDate,
          startBalance: chunk.startBalance,
          endBalance: chunk.endBalance,
        };

    (fn as any)({
      ...extraArgs,
      operation: "assignToAccount",
      transactionIds,
      params: {
        account: {
          account: isNewAccount ? account : chunk.accountName,
          isNew: isNewAccount,
        },
        period: {
          chunk: isNewAccount ? null : chunk.chunk,
          id: periodId,
          isNew: isNewAccount,
          ...periodData, // Only include these fields for new periods
        },
      },
    })
      .then(({ transactions }) => {
        dispatch(toggleLoading());
        setSubmitting(false);
        resetForm();
        toggleShowEditAccount();

        dispatch(setTls({ transactions, resetPages: false }));

        // Update data - fetchTotals will handle setting selected account/chunk
        dispatch(
          fetchTotals({
            account: isNewAccount ? account : chunk.accountName,
            chunk: isNewAccount ? periodId : chunk.chunk,
          })
        );
        dispatch(fetchMfs());
        dispatch(setSelectedRowIds({}));

        dispatch(
          createAlert({
            message: "Correction made successfully",
            type: "success",
            timeout: 3000,
          })
        );
        onDone();
      })
      .catch((err) => {
        dispatch(toggleLoading());
        setSubmitting(false);
        dispatch(
          createAlert({
            message: "Error assigning transactions to account. Please try again.",
            type: "error",
            timeout: 3000,
          })
        );
      });
  };

  const [showEditAccount, setShowEditAccount] = React.useState(false);
  const toggleShowEditAccount = () => {
    setShowEditAccount(!showEditAccount);
    setView("existing");
  };

  const dispatch = useDispatch();
  const [view, setView] = React.useState<
    "existing" | "newAccount" | "newPeriod"
  >("existing");
  const chunkData = useChunkData();

  const handleViewChange = (
    newView: "existing" | "newAccount" | "newPeriod",
    resetForm: () => void
  ) => {
    setView(newView);
    resetForm();
  };

  return (
    <>
      <Button
        className="assign-to-account-btn"
        variant="light"
        disabled={transactionIds.length === 0}
        onClick={() => {
          if (transactionIds.length === 0) {
            setSelectionError("Select at least 1 row");
            return;
          }
          toggleShowEditAccount();
        }}
      >
        <FontAwesomeIcon icon="cog" />
        <span className="ml-1">Assign To Account/Period</span>
      </Button>

      <Formik
        enableReinitialize={true}
        initialValues={{
          account: "",
          isNewAccount: false,
          startDate: "",
          endDate: "",
          startBalance: "",
          endBalance: "",
          periodId: "",
          chunk: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting,
          errors,
          touched,
          submitForm,
          setFieldValue,
          values,
          resetForm,
        }) => {
          // console.log({ values });
          return (
            <Form>
              <Modal
                size="lg"
                show={showEditAccount}
                onHide={toggleShowEditAccount}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Assign To Account/Period</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {view === "existing" && (
                    <Row className="justify-content-end">
                      <Button
                        className="m-2"
                        variant="link"
                        onClick={() =>
                          handleViewChange("newAccount", resetForm)
                        }
                      >
                        <FontAwesomeIcon icon="plus"></FontAwesomeIcon>
                        <span className="ml-1">Add Account/Period</span>
                      </Button>
                    </Row>
                  )}
                  {view === "existing" && (
                    <div>
                      <strong>
                        <Form.Label>Account/Period</Form.Label>
                      </strong>
                      <Field
                        name="chunk"
                        component={formikReactSelectInput}
                        options={chunkData.map((chunk, i) => ({
                          value: chunk,
                          label: `${chunk.accountName} from ${chunk.startDate} to ${chunk.endDate} [${chunk.documentName} ]`,
                        }))}
                        onChange={(e) => {
                          setFieldValue("isNewAccount", false);
                        }}
                        className={
                          errors.chunk && touched.chunk ? "is-invalid" : ""
                        }
                      />
                      {/* {errors.chunk && touched.chunk && (
                        <div className="invalid-feedback d-block">
                          {errors.chunk}
                        </div>
                      )} */}
                    </div>
                  )}
                  {view === "newAccount" && (
                    <div className="m-3">
                      <Button
                        className="mb-4"
                        variant="link"
                        onClick={() => handleViewChange("existing", resetForm)}
                      >
                        <FontAwesomeIcon icon="angle-left" />
                        <span className="ml-1">Back</span>
                      </Button>
                      <AccountForm2
                        onSubmit={({
                          account,
                          startDate,
                          endDate,
                          startBalance,
                          endBalance,
                        }) => {
                          setFieldValue("account", account);
                          setFieldValue("isNewAccount", true);
                          setFieldValue("periodId", uuidv4());
                          setFieldValue("startDate", startDate);
                          setFieldValue("endDate", endDate);
                          setFieldValue("startBalance", startBalance);
                          setFieldValue("endBalance", endBalance);

                          submitForm();
                        }}
                      />
                    </div>
                  )}
                </Modal.Body>
                {view === "existing" && (
                  <Modal.Footer>
                    <Button
                      onClick={() => submitForm()}
                      disabled={isSubmitting}
                    >
                      Assign
                    </Button>
                  </Modal.Footer>
                )}
              </Modal>
              <Field type="hidden" name="isNewAccount" />
              <Field type="hidden" name="periodId" />
              <Field type="hidden" name="startDate" />
              <Field type="hidden" name="endDate" />
              <Field type="hidden" name="startBalance" />
              <Field type="hidden" name="endBalance" />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default SelectAccount2;
